import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  useTheme,
  Chip,
} from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import {
  Memory,
  AttachMoney,
  CheckCircle,
  CloudQueue,
} from "@mui/icons-material";
import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";
import { toast } from "react-toastify";

const COLORS = ["#8884d8", "#82ca9d", "#ffc658", "#ff7300", "#0088fe", "#00C49F"];

const AIAnalytics = () => {
  const theme = useTheme();
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const [analytics, setAnalytics] = useState(null);

  useEffect(() => {
    fetchAnalytics();
  }, [days]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/ai/orchestrator/stats?days=${days}`);
      setAnalytics(data);
    } catch (err) {
      toast.error("Erro ao carregar analytics de IA");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(2) + "M";
    if (num >= 1000) return (num / 1000).toFixed(1) + "K";
    return num?.toLocaleString() || "0";
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "USD",
    }).format(value || 0);
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  const dailyData = analytics?.dailyUsage?.map((d) => ({
    ...d,
    date: formatDate(d.date),
    tokens: (d.requests || 0) * 500,
  })) || [];

  const providerData = analytics?.providers?.map((p) => ({
    name: p.provider,
    tokens: p.promptTokens + p.completionTokens,
    requests: p.requests,
    cost: p.costUsd,
  })) || [];

  const moduleData = analytics?.modules?.map((m) => ({
    name: m.module,
    value: m.requests,
  })) || [];

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Box>
          <Typography variant="h4" fontWeight="bold" color="primary">
            Analytics de IA
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Acompanhe o uso de tokens e custos da sua inteligencia artificial
          </Typography>
        </Box>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Periodo</InputLabel>
          <Select
            value={days}
            label="Periodo"
            onChange={(e) => setDays(e.target.value)}
          >
            <MenuItem value={7}>Ultimos 7 dias</MenuItem>
            <MenuItem value={30}>Ultimos 30 dias</MenuItem>
            <MenuItem value={60}>Ultimos 60 dias</MenuItem>
            <MenuItem value={90}>Ultimos 90 dias</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`, color: "white" }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    Total de Tokens
                  </Typography>
                  <Typography variant="h4" fontWeight="bold">
                    {formatNumber(analytics?.totalTokens || 0)}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.7 }}>
                    Prompt: {formatNumber(analytics?.totalPromptTokens || 0)} | Completion: {formatNumber(analytics?.totalCompletionTokens || 0)}
                  </Typography>
                </Box>
                <Memory sx={{ fontSize: 48, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: `linear-gradient(135deg, #4caf50 0%, #2e7d32 100%)`, color: "white" }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    Custo Total
                  </Typography>
                  <Typography variant="h4" fontWeight="bold">
                    {formatCurrency(analytics?.totalCostUsd)}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.7 }}>
                    Periodo de {days} dias
                  </Typography>
                </Box>
                <AttachMoney sx={{ fontSize: 48, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: `linear-gradient(135deg, #ff9800 0%, #f57c00 100%)`, color: "white" }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    Total Requisicoes
                  </Typography>
                  <Typography variant="h4" fontWeight="bold">
                    {formatNumber(analytics?.totalRequests || 0)}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.7 }}>
                    Media: {Math.round((analytics?.totalRequests || 0) / days)}/dia
                  </Typography>
                </Box>
                <CloudQueue sx={{ fontSize: 48, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: `linear-gradient(135deg, #2196f3 0%, #1565c0 100%)`, color: "white" }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    Taxa de Sucesso
                  </Typography>
                  <Typography variant="h4" fontWeight="bold">
                    {analytics?.successRate?.toFixed(1) || 0}%
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.7 }}>
                    Tempo medio: {Math.round(analytics?.avgProcessingTimeMs || 0)}ms
                  </Typography>
                </Box>
                <CheckCircle sx={{ fontSize: 48, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Charts Row */}
      <Grid container spacing={3} mb={4}>
        {/* Daily Usage Chart */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, height: 400 }}>
            <Typography variant="h6" gutterBottom>
              Uso Diario
            </Typography>
            <ResponsiveContainer width="100%" height="90%">
              <LineChart data={dailyData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Line
                  type="monotone"
                  dataKey="requests"
                  stroke={theme.palette.primary.main}
                  strokeWidth={2}
                  dot={{ fill: theme.palette.primary.main }}
                  name="Requisicoes"
                />
              </LineChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Module Distribution */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, height: 400 }}>
            <Typography variant="h6" gutterBottom>
              Distribuicao por Modulo
            </Typography>
            <ResponsiveContainer width="100%" height="85%">
              <PieChart>
                <Pie
                  data={moduleData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  fill="#8884d8"
                  paddingAngle={5}
                  dataKey="value"
                  label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                >
                  {moduleData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
      </Grid>

      {/* Provider Stats Table */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Estatisticas por Provider
        </Typography>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Provider</strong></TableCell>
                <TableCell align="right"><strong>Requisicoes</strong></TableCell>
                <TableCell align="right"><strong>Tokens Prompt</strong></TableCell>
                <TableCell align="right"><strong>Tokens Completion</strong></TableCell>
                <TableCell align="right"><strong>Tokens Total</strong></TableCell>
                <TableCell align="right"><strong>Custo (USD)</strong></TableCell>
                <TableCell align="right"><strong>Taxa Sucesso</strong></TableCell>
                <TableCell align="right"><strong>Tempo Medio</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {analytics?.providers?.length > 0 ? (
                analytics.providers.map((provider, index) => (
                  <TableRow key={index} hover>
                    <TableCell>
                      <Chip
                        label={provider.provider}
                        size="small"
                        sx={{
                          bgcolor: COLORS[index % COLORS.length],
                          color: "white",
                          fontWeight: "bold",
                        }}
                      />
                    </TableCell>
                    <TableCell align="right">{formatNumber(provider.requests)}</TableCell>
                    <TableCell align="right">{formatNumber(provider.promptTokens)}</TableCell>
                    <TableCell align="right">{formatNumber(provider.completionTokens)}</TableCell>
                    <TableCell align="right">
                      <strong>{formatNumber(provider.promptTokens + provider.completionTokens)}</strong>
                    </TableCell>
                    <TableCell align="right">{formatCurrency(provider.costUsd)}</TableCell>
                    <TableCell align="right">
                      <Chip
                        label={`${provider.successRate?.toFixed(1)}%`}
                        size="small"
                        color={provider.successRate >= 95 ? "success" : provider.successRate >= 80 ? "warning" : "error"}
                      />
                    </TableCell>
                    <TableCell align="right">{Math.round(provider.avgProcessingTimeMs)}ms</TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography color="textSecondary">
                      Nenhum dado de uso de IA encontrado para o periodo selecionado
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Module Stats */}
      {analytics?.modules?.length > 0 && (
        <Paper sx={{ p: 3, mt: 3 }}>
          <Typography variant="h6" gutterBottom>
            Estatisticas por Modulo
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Modulo</strong></TableCell>
                  <TableCell align="right"><strong>Requisicoes</strong></TableCell>
                  <TableCell align="right"><strong>Tokens</strong></TableCell>
                  <TableCell align="right"><strong>Custo</strong></TableCell>
                  <TableCell align="right"><strong>Taxa Sucesso</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {analytics.modules.map((mod, index) => (
                  <TableRow key={index} hover>
                    <TableCell>
                      <Chip label={mod.module} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell align="right">{formatNumber(mod.requests)}</TableCell>
                    <TableCell align="right">{formatNumber(mod.promptTokens + mod.completionTokens)}</TableCell>
                    <TableCell align="right">{formatCurrency(mod.costUsd)}</TableCell>
                    <TableCell align="right">{mod.successRate?.toFixed(1)}%</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Container>
  );
};

export default AIAnalytics;
